import { cn } from '@workspace/ui/lib/utils';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { useModal } from '~/hooks/useModal';
import { userStore } from '~/stores';

const colClassName = company.match({
  mcosmetic: 'md:w-auto',
  default: 'md:w-[235.67px]',
});

interface FooterCDNavLinksProps
  extends Omit<React.ComponentPropsWithoutRef<'section'>, 'children'> {
  variant?: 'default' | 'light';
}

export const FooterCDNavLinks: React.FC<FooterCDNavLinksProps> = ({
  variant = 'default',
  className,
  ...props
}) => {
  const { t } = useTranslation();
  const { openModal } = useModal();

  const titleClassName = cn(
    'mb-4 [font:var(--text-400-semibold)]',
    variant === 'light' ? 'text-text-primary' : 'text-text-inverse-primary',
  );

  const linkClassName = cn(
    'w-max block no-underline [font:var(--text-200-regular-high)] transition-colors duration-200',
    variant === 'light'
      ? 'text-text-secondary! hover:text-text-primary!'
      : 'text-text-inverse-secondary! hover:text-text-inverse-primary!',
    company.match({ mcosmetic: '[&+&]:mt-2', default: '[&+&]:mt-3' }),
  );

  const handleReferClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!userStore.isAuthorized) {
      openModal(ModalType.Auth, 'footer', { redirect: '/cabinet/referral' });
      event.preventDefault();
    }
  };

  return (
    <section {...props} className={cn('mt-8 md:mt-0', className)}>
      <div className="grid w-full grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-0">
        <div className={colClassName}>
          <div className={titleClassName}>{t('company')}</div>
          <Link
            data-link="footer-our-story"
            to="/our-story"
            className={linkClassName}
          >
            {t('ourStory')}
          </Link>
          <Link data-link="footer-brand" to="/brand" className={linkClassName}>
            {t('brand')}
          </Link>
          <Link
            data-link="footer-contact-us"
            to="/contact-us"
            className={linkClassName}
          >
            {t('contactUs')}
          </Link>
          <Link
            data-link="footer-careers"
            to="/careers"
            className={linkClassName}
          >
            {t('careers')}
          </Link>
          {company.match({
            mcosmetic: (
              <Link
                data-link="footer-terms-of-service"
                to="/legals"
                className={linkClassName}
              >
                {t('termsOfService')}
              </Link>
            ),
            default: (
              <Link
                data-link="footer-legals"
                to="/legals"
                state={{ source: 'home' }}
                className={linkClassName}
              >
                {t('legals')}
              </Link>
            ),
          })}
        </div>

        <div className={colClassName}>
          <div className={titleClassName}>{t('media')}</div>
          <Link
            data-link="footer-influencers"
            to="/influencers"
            className={linkClassName}
          >
            {t('influencers')}
          </Link>
          <Link
            data-link="footer-blog"
            to={company.config.links.blog ?? ''}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
          >
            {t('blog')}
          </Link>
        </div>

        <div className={cn(colClassName, 'max-md:col-span-2')}>
          <div className={titleClassName}>{t('information')}</div>
          <Link
            data-link="footer-shipping"
            to="/shipping-return"
            className={linkClassName}
          >
            {t('policies')}
          </Link>
          <Link
            data-link="footer-refound"
            to="/refund"
            className={linkClassName}
          >
            {t('refundPolicy')}
          </Link>
          <Link data-link="footer-faq" to="/faq" className={linkClassName}>
            {t('faq')}
          </Link>
          <Link
            data-link="footer-referral"
            to="/cabinet/referral"
            onClick={handleReferClick}
            className={linkClassName}
          >
            {t('referAfriend')}
          </Link>
        </div>
      </div>
    </section>
  );
};
